/**
 * Substitutes `:param` placeholders in a redirect target with values from a route match.
 *
 * Only tokens the route match actually provides are substituted. Redirect targets can
 * contain literal colons — experience offer ids are shaped `led:006RF00000d6skAYAQ` — and
 * those must survive untouched, so an unrecognised `:word` is returned as written.
 *
 * @example
 * interpolateRedirectPath('/:regionCode/account', { regionCode: 'au' })
 * // => '/au/account'
 *
 * @example Absent optional param collapses, along with its now-empty segment
 * interpolateRedirectPath('/:regionCode/account/my-escapes/:modalType?', { regionCode: 'au', modalType: undefined })
 * // => '/au/account/my-escapes'
 *
 * @example Literal colon preserved
 * interpolateRedirectPath('/au/experience/high-tea/led:006RF00000d6skAYAQ', { regionCode: 'au' })
 * // => '/au/experience/high-tea/led:006RF00000d6skAYAQ'
 */
export function interpolateRedirectPath(
  value: string,
  params: Record<string, string | undefined>,
): string {
  const interpolated = value.replace(/:([A-Za-z0-9_]+)(\?)?/g, (token, key: string, optional?: string) => {
    const param = params[key]

    if (param) {
      return encodeURIComponent(param)
    }

    // A param the route declared but did not capture collapses away, as does an absent
    // optional one.
    if (optional || key in params) {
      return ''
    }

    // Anything else is a literal colon in the target rather than a placeholder.
    return token
  })

  if (!value.startsWith('/')) {
    return interpolated
  }

  // Collapse the gaps left by dropped segments, and any resulting trailing slash.
  return interpolated
    .replace(/\/{2,}/g, '/')
    .replace(/\/(?=[?#]|$)/, '') || '/'
}
